import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DimensionValue, Platform, StyleSheet, Text, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';

import { getActivePlayers } from '../../../services/api';
import { getSocket } from '../../../services/socket';
import { RootState } from '../../../store';
import { selectIsLogged } from '../../../store/auth/authReducer';
import { setActivePlayers } from '../../../store/invites/inviteSlice'; // Import the action
import { env } from '../../../utils/env';
import { gameHelixConfig } from '../../../utils/helpers/gameHelixConfig';
import { localizeGameSocketPayload } from '../../../utils/helpers/localizeTableName';
import { resolveSvaraTableLevel } from '../../../utils/helpers/resolveSvaraTableLevel';
import { useTableOwner } from '../../../utils/hooks/useTableOwner';
import { RootStackParamList } from '../../navigator';
import { isInsufficientGiftBalancePacket } from './giftAckUtils';
import {
	GameHostApi,
	GameInitData,
	GameTableProps,
	PAY_TO_PLAY_MIN_LEVEL,
	SocketPayload,
} from './types';

let globalJustSwitchedTable = false;

/** Plain CSS for DOM iframe (avoid RN StyleSheet vs CSSProperties mismatch) */
const gameIframeStyle: React.CSSProperties = {
	position: 'absolute',
	top: 0,
	left: 0,
	right: 0,
	bottom: 0,
	width: '100%',
	height: '100%',
	border: 'none',
	backgroundColor: '#420304',
};

type NavigationProp = NativeStackNavigationProp<RootStackParamList, 'Game'>;

type RawSocket = NonNullable<ReturnType<typeof getSocket>>;
type SocketListener = Parameters<RawSocket['on']>[1];
type SocketEvent = Parameters<RawSocket['on']>[0];
type SocketOffEvent = Parameters<RawSocket['off']>[0];
type SocketOffListener = Parameters<RawSocket['off']>[1];
type SocketRemoveAllEvent = Parameters<RawSocket['removeAllListeners']>[0];
type SocketEmitEvent = Parameters<RawSocket['emit']>[0];
type SocketEmitArgs = Parameters<RawSocket['emit']> extends [string, ...infer Args] ? Args : never;

interface SocketLike {
	id?: RawSocket['id'];
	connected?: RawSocket['connected'];
	on: (event: SocketEvent, listener: SocketListener) => SocketLike;
	once: (event: SocketEvent, listener: SocketListener) => SocketLike;
	addListener?: (event: SocketEvent, listener: SocketListener) => SocketLike;
	off: (event?: SocketOffEvent, listener?: SocketOffListener) => SocketLike;
	removeListener?: (event?: SocketOffEvent, listener?: SocketOffListener) => SocketLike;
	emit: (event: SocketEmitEvent, ...args: SocketEmitArgs) => ReturnType<RawSocket['emit']>;
	removeAllListeners: (event?: SocketRemoveAllEvent) => SocketLike;
}

const GameTable: React.FC<GameTableProps> = ({
	tableId,
	legacyRoomId,
	roomSlug,
	isTutorial = false,
	onGameEvent,
	isTournamentRoom = false,
	isDuel = false,
	hostApiRef,
}) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const iframeRef = useRef<HTMLIFrameElement | null>(null);

	useEffect(() => {
		if (!hostApiRef) return;
		const gameApi = () =>
			(iframeRef.current?.contentWindow as (Window & { svaraGame?: Partial<GameHostApi> }) | null)
				?.svaraGame;
		hostApiRef.current = {
			toggleSound: () => gameApi()?.toggleSound?.(),
			openSettings: () => gameApi()?.openSettings?.(),
		};
		return () => {
			hostApiRef.current = null;
		};
	}, [hostApiRef]);
	const [gameStarted, setGameStarted] = useState(false);
	const [gameData, setGameData] = useState<GameInitData | null>(null);
	const [embedReady, setEmbedReady] = useState(false);
	// Ref mirror so socket listeners and effect cleanups see the current value
	// rather than the one captured when they were registered.
	const gameStartedRef = useRef(false);
	useEffect(() => {
		gameStartedRef.current = gameStarted;
	}, [gameStarted]);

	console.log('🌐 GameTable FILE = web, Platform =', Platform.OS);

	// Get auth data from Redux store
	const auth = useSelector((state: RootState) => state.auth);
	const i18nBundle = useSelector((state: RootState) => state.i18n.bundle);
	const langCode = useSelector((state: RootState) => state.i18n.langCode);

	const isLogged = useSelector(selectIsLogged);
	const socket = useMemo(() => {
		if (!auth.user?.id || !auth.user?.username) {
			return undefined;
		}
		return getSocket(String(auth.user.id), String(auth.user.username));
	}, [auth.user?.id, auth.user?.username]);
	const navigation = useNavigation<NavigationProp>();

	/**
	 * Owner of this table, needed in initData before startGame: it gates inviting
	 * into an empty seat and the KICK button. Tournaments have no owner and the
	 * tutorial has no server table, so neither is looked up.
	 */
	const { ownerId, resolved: ownerResolved } = useTableOwner(tableId, {
		enabled: !isTutorial && !isTournamentRoom,
	});

	const createIframeSafeSocket = useCallback((rawSocket: RawSocket) => {
		const listenerMap = new WeakMap<SocketListener, SocketListener>();

		const cloneForFrame = (value: SocketPayload | undefined) => {
			if (value === null || value === undefined) return value;
			if (typeof value !== 'object') return value;

			try {
				return JSON.parse(JSON.stringify(value));
			} catch {
				return value;
			}
		};

		const wrapListener = (event: SocketEvent, listener: SocketListener) => {
			const wrapped: SocketListener = (...args: Parameters<SocketListener>) => {
				const mappedArgs = args.map(arg =>
					localizeGameSocketPayload(String(event), cloneForFrame(arg as SocketPayload | undefined)),
				);
				listener(...(mappedArgs as Parameters<SocketListener>));
			};
			listenerMap.set(listener, wrapped);
			return wrapped;
		};

		const bridge: SocketLike = {
			get id() {
				return rawSocket.id;
			},
			get connected() {
				return rawSocket.connected;
			},
			on(event: SocketEvent, listener: SocketListener) {
				rawSocket.on(event, wrapListener(event, listener));
				return this;
			},
			once(event: SocketEvent, listener: SocketListener) {
				rawSocket.once(event, wrapListener(event, listener));
				return this;
			},
			addListener(event: SocketEvent, listener: SocketListener) {
				return this.on(event, listener);
			},
			off(event?: SocketOffEvent, listener?: SocketOffListener) {
				if (!listener) {
					rawSocket.off(event);
					return this;
				}

				const mappedListener = listener as SocketListener;
				const wrapped = listenerMap.get(mappedListener) || mappedListener;
				rawSocket.off(event, wrapped);
				listenerMap.delete(mappedListener);
				return this;
			},
			removeListener(event?: SocketOffEvent, listener?: SocketOffListener) {
				return this.off(event, listener);
			},
			removeAllListeners(event?: SocketRemoveAllEvent) {
				rawSocket.removeAllListeners(event);
				return this;
			},
			emit(event: SocketEmitEvent, ...args: SocketEmitArgs) {
				return rawSocket.emit(event, ...args);
			},
		};

		if (typeof Proxy === 'function') {
			return new Proxy(bridge, {
				get(target: SocketLike, prop: PropertyKey, receiver: object) {
					if (prop in target) {
						const value = Reflect.get(target, prop, receiver as object);
						return typeof value === 'function' ? value.bind(target) : value;
					}

					const value = Reflect.get(rawSocket as object, prop);
					return typeof value === 'function' ? value.bind(rawSocket) : value;
				},
			});
		}

		return bridge;
	}, []);

	useEffect(() => {
		if (!isLogged || !auth.user?.id || !auth.user?.username) {
			console.log('[TOURNAMENT] QuickSeat web: Waiting for auth...', { isTournamentRoom });
			return;
		}
		const quickSeatSocket = getSocket(String(auth.user.id), String(auth.user.username));

		if (!quickSeatSocket) {
			console.log('[TOURNAMENT] QuickSeat web: No socket available');
			return;
		}

		type QuickSeatData = { tableId?: string | number; roomSlug?: string };
		type QuickSeatPacket = { success?: boolean; data?: QuickSeatData } & Partial<QuickSeatData>;

		const handleQuickSeat = (packet: QuickSeatPacket) => {
			console.log('[TOURNAMENT] QuickSeat web: received', {
				packet,
				currentTableId: tableId,
				isTournamentRoom,
				gameStarted,
			});

			const typedPacket = packet ?? {};
			const data = typedPacket.data || typedPacket;

			if (typedPacket.success && data.tableId && data.tableId !== tableId) {
				console.log('[TOURNAMENT] QuickSeat web: SWITCHING tables', {
					fromTableId: tableId,
					toTableId: data.tableId,
					isTournamentRoom,
					wasTournament: isTournamentRoom,
					willBeTournament: false,
				});
				globalJustSwitchedTable = true;
				if (gameStarted) {
					try {
						(
							iframeRef.current?.contentWindow as { destroyGame?: () => void } | undefined
						)?.destroyGame?.();
						setGameStarted(false);
					} catch (error) {
						console.error('[TOURNAMENT] Failed to destroy game:', error);
					}
				}

				const nextRoomSlug = data.roomSlug || roomSlug;
				console.log('[TOURNAMENT] Navigation replace triggered by quickSeat (web)', {
					tableId: data.tableId,
					roomSlug: nextRoomSlug,
					isTournamentRoom: false,
				});
				navigation.replace('Game', {
					tableId: data.tableId,
					roomSlug: nextRoomSlug,
					legacyRoomId: resolveSvaraTableLevel(undefined, nextRoomSlug),
					isTournamentRoom: false,
				});
				setTimeout(() => {
					globalJustSwitchedTable = false;
				}, 2000);
			} else if (typedPacket.success && data.tableId === tableId) {
				console.log('[TOURNAMENT] QuickSeat web: same table, no navigation');
			}
		};

		quickSeatSocket.on('quickSeat', handleQuickSeat);
		console.log('[TOURNAMENT] QuickSeat web: listener registered', { isTournamentRoom, tableId });

		return () => {
			quickSeatSocket.off('quickSeat', handleQuickSeat);
			console.log('[TOURNAMENT] QuickSeat web: listener removed', { isTournamentRoom, tableId });
		};
	}, [isLogged, auth.user, tableId, gameStarted, navigation, roomSlug, isTournamentRoom]);

	/** ---------------------------
	 *  V1-PARITY SOCKET GLUE
	 *  Mirrors Svara-FRONTEND table.component.ts: keep the wallet/level/VIP state
	 *  the game shows current after seat and chip changes, re-join and refetch the
	 *  table after a reconnect or tab foreground (room membership is per-socket, so
	 *  without this a resumed session never receives another update), and sit the
	 *  player out when they leave instead of leaving them seated.
	 *  --------------------------- */
	useEffect(() => {
		if (!socket || !isLogged) return;
		if (isTutorial) return; // the tutorial plays locally, there is no server table

		const prefix = isTournamentRoom ? 'tournament:' : 'svara:';

		const preserveUser = () => {
			try {
				socket.emit('service:preserveUser', { sid: socket.id });
			} catch {}
		};

		socket.on('takeSeat', preserveUser);
		socket.on('exitTable', preserveUser);
		socket.on('buyIn', preserveUser);

		const resync = () => {
			if (!gameStartedRef.current || tableId == null) return;
			try {
				if (!isTournamentRoom) preserveUser();
				socket.emit(`${prefix}joinTable`, { tableId });
				socket.emit(`${prefix}getTimeNow`, {});
				socket.emit(`${prefix}getTable`, { tableId });
			} catch {}
		};

		const onConnect = () => resync();
		socket.on('connect', onConnect);

		const onVisibilityChange = () => {
			// Only resync over a live connection; with the socket down 'connect'
			// resyncs once it comes back anyway.
			if (!document.hidden && socket.connected) resync();
		};
		document.addEventListener('visibilitychange', onVisibilityChange);

		return () => {
			socket.off('takeSeat', preserveUser);
			socket.off('exitTable', preserveUser);
			socket.off('buyIn', preserveUser);
			socket.off('connect', onConnect);
			document.removeEventListener('visibilitychange', onVisibilityChange);

			// v1 emitted this from ngOnDestroy for cash tables only; the tournament
			// host deliberately did not.
			if (!isTournamentRoom && tableId != null && gameStartedRef.current) {
				try {
					socket.emit('svara:playerSitOut', { tableId });
				} catch {}
			}
		};
	}, [socket, isLogged, isTutorial, isTournamentRoom, tableId]);

	/** Iframe loads /svaragame/embed.html so relative asset URLs resolve under /svaragame/ */
	useEffect(() => {
		const onMessage = (e: MessageEvent) => {
			const isFromIframe = e.source === iframeRef.current?.contentWindow;
			const isSameOrigin = e.origin === window.location.origin;

			if (!isFromIframe && !isSameOrigin) {
				return;
			}
			if (e.data?.type === 'SVARA_EMBED_READY') {
				setEmbedReady(true);
			}
			if (e.data?.type === 'SVARA_GAME_EVENT' && typeof e.data.name === 'string') {
				window.dispatchEvent(new CustomEvent(e.data.name, { detail: e.data.detail }));
			}
		};
		window.addEventListener('message', onMessage);
		return () => window.removeEventListener('message', onMessage);
	}, []);

	useEffect(() => {
		const handleHashChange = () => {
			if (globalJustSwitchedTable) {
				console.log('[TOURNAMENT] Ignoring hash change - just switched tables');
				window.history.replaceState(null, '', window.location.pathname);
				return;
			}

			const hash = window.location.hash;
			console.log('[TOURNAMENT] Hash changed on web', {
				hash,
				isTournamentRoom,
				tableId,
				gameStarted,
			});

			const roomMatch = hash.match(/#\/room\/([^/]+)/);
			const lobbyMatch = hash === '#/lobby' || hash.startsWith('#/lobby');
			if (roomMatch && onGameEvent) {
				const hashRoomSlug = roomMatch[1];
				console.log('[TOURNAMENT] CRITICAL: Detected room hash on web - FIRING goToLobby', {
					hashRoomSlug,
					isTournamentRoom,
					tableId,
				});

				window.history.replaceState(null, '', window.location.pathname);
				onGameEvent('goToLobby', { roomSlug: hashRoomSlug, roomId: hashRoomSlug });
			} else if (lobbyMatch && onGameEvent) {
				console.log('[TOURNAMENT] CRITICAL: Detected lobby hash on web - FIRING goToLobby', {
					isTournamentRoom,
					tableId,
				});
				window.history.replaceState(null, '', window.location.pathname);
				onGameEvent('goToLobby');
			}
		};

		window.addEventListener('hashchange', handleHashChange);

		return () => {
			window.removeEventListener('hashchange', handleHashChange);
		};
	}, [onGameEvent, gameStarted, isTournamentRoom, tableId]);

	// Check authentication and redirect to login if not logged in
	useEffect(() => {
		if (!isLogged) {
			console.log('🔐 User not logged in, redirecting to login...');
			navigation.navigate('Login' as never);
		}
	}, [isLogged, navigation]);

	// Initialize game data
	useEffect(() => {
		console.log('🎮 GameTable auth effect triggered:', { auth, isLogged });

		if (!isLogged || !auth.token) {
			console.log('⏳ Waiting for authentication...');
			return;
		}

		// Translations have to be final before the game starts: it treats whatever
		// object it is handed as the complete bundle, so an empty one means the table
		// renders raw T_* keys for the rest of the session.
		if (!i18nBundle || Object.keys(i18nBundle).length === 0) {
			console.log('⏳ Waiting for translations...');
			return;
		}

		// ownerId gates the owner-only affordances and is read once at startGame,
		// so the lookup has to have settled first. It always settles, success or
		// not, so this cannot wedge the table.
		if (!ownerResolved) {
			console.log('⏳ Waiting for table owner lookup...');
			return;
		}

		// Get proper game dimensions like the Angular app
		const getGameDimensions = () => {
			let ratio = 1;
			const w = Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
			const h = Math.max(document.documentElement.clientHeight, window.innerHeight || 0);

			if (
				// @ts-ignore
				window.screen.systemXDPI !== undefined &&
				// @ts-ignore
				window.screen.logicalXDPI !== undefined &&
				// @ts-ignore
				window.screen.systemXDPI > window.screen.logicalXDPI
			) {
				// @ts-ignore
				ratio = window.screen.systemXDPI / window.screen.logicalXDPI;
			} else if (window.devicePixelRatio !== undefined) {
				ratio = window.devicePixelRatio;
			}

			console.log('🎮 Game dimensions:', { width: w, height: h, ratio });
			return { width: w, height: h, ratio };
		};

		const dimensions = getGameDimensions();
		const tableLevel = resolveSvaraTableLevel(legacyRoomId, roomSlug);

		const gameInitData = {
			userId: auth.user?.numericId,
			tableId,
			width: dimensions.width * dimensions.ratio,
			height: dimensions.height * dimensions.ratio,
			ownerId: ownerId ?? undefined,
			tutorial: isTutorial,
			isVipRoom: roomSlug === 'vip',
			isTournamentRoom: isTournamentRoom,
			isDuelRoom: isDuel,
			tableLevel,
			playerName: auth.user?.username,
			soundEnabled:
				auth.user?.isSoundEffectsEnabled !== undefined ? auth.user.isSoundEffectsEnabled : true,
			translations: i18nBundle || {},
			language: langCode || 'en',
			drawingBuffer: false,
			payToPlay: (auth.user?.level || 1) >= PAY_TO_PLAY_MIN_LEVEL,
			freeToPlay: false,
			payToPlayMinLevel: PAY_TO_PLAY_MIN_LEVEL,
			token: auth.token, // Use the actual token from auth
			api: {
				game: `${env('SVARA_API_URL')}/api/game` || 'https://play.vipsvara.com/api/game',
				user: `${env('SVARA_API_URL')}/api/user` || 'https://play.vipsvara.com/api/user',
			},
			eventRunning: '',
			isEventRunning: false,
			powerMeter: !!auth.user?.isHandPowerEnabled,
			helix: gameHelixConfig(),
		};

		console.log('🎮 Auth object:', auth);
		console.log('🎮 Auth token:', auth.token);
		console.log('🎮 Auth user:', auth.user);
		console.log('🎮 Game init data:', gameInitData);
		setGameData(gameInitData);
	}, [
		auth,
		isLogged,
		tableId,
		isTutorial,
		i18nBundle,
		isTournamentRoom,
		isDuel,
		langCode,
		legacyRoomId,
		roomSlug,
		ownerId,
		ownerResolved,
	]);

	// Handle game events from PIXI
	useEffect(() => {
		const handleGameEvents = (event: CustomEvent) => {
			console.log('[TOURNAMENT] Game event from PIXI (web)', {
				type: event.type,
				detail: event.detail,
				isTournamentRoom,
				tableId,
			});
			switch (event.type) {
				case 'becomeVIP':
					onGameEvent?.('becomeVIP');
					break;
				case 'notEnoughChips':
					onGameEvent?.('notEnoughChips');
					break;
				case 'buyChips':
					onGameEvent?.('buyChips');
					break;
				case 'invite':
					const fetchAndShowInviteModal = async () => {
						try {
							const activePlayers = await getActivePlayers({
								start: 0,
								limit: 100,
								tableId: tableId,
							});
							console.log('🔍 Fetched active players count:', activePlayers.data.items.length);
							console.log('Active players fetched:', activePlayers);
							dispatch(setActivePlayers(activePlayers.data.items));
							onGameEvent?.('invite', activePlayers);
						} catch (error) {
							console.error('Failed to fetch active players:', error);
							onGameEvent?.('invite', []);
						}
					};
					fetchAndShowInviteModal();
					break;

				case 'setSound':
					onGameEvent?.('setSound', event.detail);
					break;
				case 'openNotifications':
					onGameEvent?.('openNotifications');
					break;
				case 'showBlackscreenAlert':
					onGameEvent?.('showBlackscreenAlert');
					break;
				case 'hideBlackscreenAlert':
					onGameEvent?.('hideBlackscreenAlert');
					break;
				case 'tableDoesNotExist':
					onGameEvent?.('tableDoesNotExist');
					break;
				case 'localPlayerTurnStarted':
					onGameEvent?.('localPlayerTurnStarted');
					break;
			}
		};

		const handleInviteReceived = (data: SocketPayload) => {
			console.log('Received invite:', data);
			onGameEvent?.('inviteReceived', data);
		};

		const handleGiftSendAck = (packet: SocketPayload) => {
			if (isInsufficientGiftBalancePacket(packet)) {
				onGameEvent?.('notEnoughChips');
			}
		};

		// Add event listeners
		const events = [
			'becomeVIP',
			'notEnoughChips',
			'buyChips',
			'invite',
			'setSound',
			'openNotifications',
			'showBlackscreenAlert',
			'hideBlackscreenAlert',
			'tableDoesNotExist',
			'localPlayerTurnStarted',
		];

		events.forEach(eventType => {
			window.addEventListener(eventType as keyof WindowEventMap, handleGameEvents as EventListener);
		});

		if (socket) {
			socket.on('inviteUser', handleInviteReceived);
			socket.on('giftSendingAck', handleGiftSendAck);
			socket.on('winGiftSendingAck', handleGiftSendAck);
		}

		return () => {
			events.forEach(eventType => {
				window.removeEventListener(
					eventType as keyof WindowEventMap,
					handleGameEvents as EventListener,
				);
			});
			socket?.off('inviteUser', handleInviteReceived);
			socket?.off('giftSendingAck', handleGiftSendAck);
			socket?.off('winGiftSendingAck', handleGiftSendAck);
		};
	}, [onGameEvent, dispatch, socket, tableId, isTournamentRoom]);

	// Start PIXI game inside iframe (same-origin: can assign socket and call startGame)
	useEffect(() => {
		console.log('🎮 GameTable useEffect triggered:', {
			gameData: !!gameData,
			embedReady,
			gameStarted,
		});

		if (!gameData) {
			console.log('⏳ Waiting for game data...');
			return;
		}

		if (!embedReady) {
			console.log('⏳ Waiting for /svaragame/embed.html...');
			return;
		}

		if (gameStarted) {
			console.log('⏳ Game already started');
			return;
		}

		const attemptStartGame = (): boolean => {
			const win = iframeRef.current?.contentWindow as
				| (Window & { startGame?: (data: GameInitData) => void; socket?: SocketLike })
				| undefined;
			if (!win?.startGame) {
				console.error('❌ iframe startGame is not available!');
				return false;
			}

			try {
				if (socket) {
					win.socket = createIframeSafeSocket(socket);
					console.log('🔌 Socket made available to PIXI game (iframe)');
				}

				console.log('🎮 Starting PIXI game with data:', gameData);
				win.startGame(gameData);
				setGameStarted(true);
				console.log('🎮 PIXI game started successfully');

				return true;
			} catch (error) {
				console.error('Failed to start game:', error);
				return false;
			}
		};

		if (attemptStartGame()) {
			return;
		}

		const retryDelays = [100, 200, 500, 1000];
		let retryCount = 0;

		const retryStartGame = () => {
			if (retryCount >= retryDelays.length) {
				console.error('❌ Failed to start game after all retries');
				return;
			}

			const delay = retryDelays[retryCount];
			console.log(`⏳ Retrying game start in ${delay}ms (attempt ${retryCount + 1})`);

			setTimeout(() => {
				if (attemptStartGame()) {
					return;
				}
				retryCount++;
				retryStartGame();
			}, delay);
		};

		retryStartGame();
	}, [gameData, embedReady, socket, gameStarted, createIframeSafeSocket]);

	// Cleanup on unmount
	useEffect(() => {
		const iframe = iframeRef.current;
		return () => {
			if (!gameStarted) {
				return;
			}
			const win = iframe?.contentWindow as (Window & { destroyGame?: () => void }) | undefined;
			if (win?.destroyGame) {
				try {
					win.destroyGame();
				} catch (error) {
					console.error('Failed to destroy game:', error);
				}
			}
		};
	}, [gameStarted]);

	const handleIframeLoad = useCallback(() => {
		console.log('[TOURNAMENT] Iframe loaded', { isTournamentRoom, tableId, gameStarted });
		setEmbedReady(true);

		try {
			const frameLocation = iframeRef.current?.contentWindow?.location;
			if (!frameLocation) return;

			const pathname = frameLocation.pathname || '';
			const isLobbyNavigation =
				pathname === '/svaragame' || pathname === '/svaragame/' || pathname.endsWith('embed.htm');

			if (isLobbyNavigation) {
				console.log('[TOURNAMENT] CRITICAL: Iframe loaded lobby nav - FIRING goToLobby', {
					pathname,
					isTournamentRoom,
					tableId,
				});
				onGameEvent?.('goToLobby');
			}
		} catch {
			// ignore cross-origin/transient iframe read errors
		}
	}, [onGameEvent, gameStarted, isTournamentRoom, tableId]);

	return (
		<View style={[styles.container, isDuel && styles.containerFillParent]}>
			{!embedReady && (
				<View style={styles.loadingContainer}>
					<Text style={styles.loadingText}>
						{t('T_SITE_GAME_LOADING', { defaultValue: 'Loading game...' })}
					</Text>
				</View>
			)}
			{/* RN-web often gives %/flex children 0 height; absolute fill + minHeight on #root chain fixes iframe document height */}
			<iframe
				ref={iframeRef}
				src='/svaragame/embed.html'
				onLoad={handleIframeLoad}
				title='Svara game'
				style={gameIframeStyle}
			/>
		</View>
	);
};

const styles = StyleSheet.create({
	container: {
		flex: 1,
		alignSelf: 'stretch',
		position: 'relative',
		width: '100%',
		minHeight: '100dvh' as DimensionValue,
		height: '100dvh' as DimensionValue,
	},
	containerFillParent: {
		minHeight: 0,
		height: '100%' as DimensionValue,
	},
	loadingContainer: {
		position: 'absolute',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		backgroundColor: '#000',
		justifyContent: 'center',
		alignItems: 'center',
		zIndex: 1000,
	},
	loadingText: {
		color: '#fff',
		fontSize: 18,
		fontWeight: 'bold',
	},
});

export default GameTable;
